import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import LocationService from '../../services/locationService';
import Button from '../common/Button';
import ToastContainer from '../common/ToastContainer';
import { useToast } from '../../hooks/useToast';
import './Locations.css';

const Locations = () => {
  const auth = useAuth();
  const { toasts, removeToast, success: showSuccess, error: showError } = useToast();
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [deletingLocation, setDeletingLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    color: '#4299E1'
  });

  const loadLocations = useCallback(async () => {
    try {
      setInitialLoading(true);
      const token = auth.user?.access_token;
      const response = await LocationService.getAllLocations(token);
      setLocations(response.data || []);
    } catch (error) {
      showError('Error al cargar las ubicaciones');
    } finally {
      setInitialLoading(false);
    }
  }, [auth.user?.access_token, showError]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  useEffect(() => {
    if (!searchFilter.trim()) {
      setFilteredLocations(locations);
    } else {
      const filtered = locations.filter((location) =>
        location.name.toLowerCase().includes(searchFilter.toLowerCase())
      );
      setFilteredLocations(filtered);
    }
  }, [searchFilter, locations]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearchChange = (e) => {
    setSearchFilter(e.target.value);
  };

  const clearSearch = () => {
    setSearchFilter('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const token = auth.user?.access_token;

      if (editingLocation) {
        await LocationService.updateLocation(editingLocation.id, formData, token);
        showSuccess('Ubicación actualizada exitosamente');
      } else {
        await LocationService.createLocation(formData, token);
        showSuccess('Ubicación creada exitosamente');
      }

      await loadLocations();
      handleCloseModal();
    } catch (error) {
      const fallbackMessage = editingLocation
        ? 'Error al actualizar la ubicación'
        : 'Error al crear la ubicación';
      showError(error?.message || fallbackMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddLocation = () => {
    setEditingLocation(null);
    setFormData({ name: '', color: '#4299E1' });
    setShowModal(true);
  };

  const handleEditLocation = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      color: location.color
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingLocation(null);
    setFormData({ name: '', color: '#4299E1' });
  };

  const handleDeleteLocation = (location) => {
    setDeletingLocation(location);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingLocation(null);
  };

  const confirmDelete = async () => {
    if (!deletingLocation) return;

    setIsLoading(true);

    try {
      const token = auth.user?.access_token;
      await LocationService.deleteLocation(deletingLocation.id, token);

      showSuccess('Ubicación eliminada exitosamente');

      await loadLocations();
      handleCloseDeleteModal();
    } catch (error) {
      showError(error?.message || 'Error al eliminar la ubicación');
    } finally {
      setIsLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="locations-container">
        <div className="locations-loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="locations-container">
      <div className="locations-header">
        <div className="header-content">
          <h1>Ubicaciones</h1>
          <p>Gestiona los cubículos disponibles para atender pacientes</p>
        </div>
        <button className="btn-primary add-btn" onClick={handleAddLocation}>
          Agregar Ubicación
        </button>
      </div>

      <div className="locations-filters">
        <div className="search-container">
          <input
            type="text"
            placeholder="Buscar por nombre de ubicación..."
            value={searchFilter}
            onChange={handleSearchChange}
            className="search-input"
          />
          {searchFilter && (
            <button className="clear-search-btn" onClick={clearSearch} title="Limpiar búsqueda">
              ✕
            </button>
          )}
        </div>
      </div>

      <div className="locations-table-container">
        {filteredLocations.length === 0 ? (
          <div className="no-locations">
            <p>No se encontraron ubicaciones</p>
            {searchFilter && (
              <button className="btn-secondary" onClick={clearSearch}>
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <>
            <table className="locations-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Color</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredLocations.map((location) => (
                  <tr key={location.id}>
                    <td className="location-name">
                      <div className="location-details">
                        <span className="name">{location.name}</span>
                      </div>
                    </td>
                    <td className="location-color-cell">
                      <div className="location-color-display">
                        <span
                          className="location-color-dot"
                          style={{ backgroundColor: location.color }}
                        ></span>
                      </div>
                    </td>
                    <td className="location-actions">
                      <button
                        className="btn-secondary edit-btn"
                        onClick={() => handleEditLocation(location)}
                        title="Editar ubicación"
                      >
                        Editar
                      </button>
                      <button
                        className="btn-danger delete-btn"
                        onClick={() => handleDeleteLocation(location)}
                        title="Eliminar ubicación"
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="locations-mobile-cards">
              {filteredLocations.map((location) => (
                <div key={location.id} className="location-card">
                  <div className="location-card-header">
                    <div className="location-card-title">{location.name}</div>
                  </div>
                  <div className="location-card-content">
                    <div className="card-detail">
                      <strong>Color:</strong>
                      <span
                        className="location-color-dot"
                        style={{ backgroundColor: location.color }}
                      ></span>
                    </div>
                  </div>
                  <div className="location-card-actions">
                    <button
                      className="btn-secondary edit-btn"
                      onClick={() => handleEditLocation(location)}
                      title="Editar ubicación"
                    >
                      Editar
                    </button>
                    <button
                      className="btn-danger delete-btn"
                      onClick={() => handleDeleteLocation(location)}
                      title="Eliminar ubicación"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingLocation ? 'Editar Ubicación' : 'Agregar Nueva Ubicación'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="name">
                    Nombre de la Ubicación <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Ej: Cubículo 1"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="color">
                    Color <span className="required">*</span>
                  </label>
                  <div className="location-color-input-group">
                    <input
                      type="color"
                      id="color"
                      name="color"
                      value={formData.color}
                      onChange={handleInputChange}
                      required
                      className="location-color-picker"
                    />
                    <input
                      type="text"
                      name="color"
                      value={formData.color}
                      onChange={handleInputChange}
                      required
                      pattern="^#([A-Fa-f0-9]{6})$"
                      placeholder="#4299E1"
                    />
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleCloseModal}
                  disabled={isLoading}
                >
                  Cancelar
                </button>
                <Button type="submit" variant="primary" loading={isLoading} icon="fas fa-save">
                  {isLoading
                    ? editingLocation
                      ? 'Actualizando...'
                      : 'Creando...'
                    : editingLocation
                    ? 'Actualizar'
                    : 'Crear'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal delete-modal">
            <div className="modal-header">
              <h3>Confirmar Eliminación</h3>
              <button className="modal-close" onClick={handleCloseDeleteModal}>
                ✕
              </button>
            </div>

            <div className="modal-content">
              <p>
                ¿Estás seguro de que deseas eliminar la ubicación <strong>"{deletingLocation?.name}"</strong>?
              </p>
              <p className="warning-text">Esta acción no se puede deshacer.</p>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCloseDeleteModal}
                disabled={isLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={confirmDelete}
                disabled={isLoading}
              >
                {isLoading ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default Locations;
